<template>
  <section class="schedule-page">
    <div v-if="isLoading" class="loading-overlay" role="status" aria-live="polite">
      <div class="loading-box">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p>Consultando roteiros disponíveis...</p>
      </div>
    </div>

    <div class="schedule-shell">
      <header class="schedule-header">
        <div>
          <p class="eyebrow">Agenda de visitação</p>
          <h1>Escolha um roteiro disponível</h1>
          <p class="header-copy">
            Selecione uma data, compare os horários e reserve o roteiro mais adequado para o seu grupo.
          </p>
          <p class="service-notice">
            Sistema exclusivo para agendamento de visitas ao Museu Catavento em São Paulo - SP, Capital.
          </p>
        </div>

        <div class="user-panel">
          <span>{{ eadminsera ? 'Acesso administrativo' : 'E-mail autenticado' }}</span>
          <strong>{{ email }}</strong>
        </div>
      </header>

      <section class="date-panel" aria-label="Consulta de data">
        <button
          type="button"
          class="date-nav-button"
          :disabled="!podeVoltarData"
          title="Dia anterior"
          @click="mudarData(-1)"
        >
          ‹
        </button>

        <div class="date-field">
          <label for="dataselec">Data da visita</label>
          <input
            v-if="eadminsera"
            id="dataselec"
            type="date"
            v-model="tempDate"
            @change="atualizarData"
          />
          <input
            v-else
            id="dataselec"
            type="date"
            :min="minDate"
            v-model="tempDate"
            @keydown.prevent
            @change="atualizarData"
          />
        </div>

        <div v-if="selectedDate" class="selected-date">
          <span>Data selecionada</span>
          <strong>{{ formattedSelectedDate }}</strong>
          <small>{{ dayOfWeek }}</small>
        </div>

        <button
          type="button"
          class="date-nav-button"
          title="Próximo dia"
          @click="mudarData(1)"
        >
          ›
        </button>

        <div class="quick-dates" aria-label="Datas rápidas">
          <button
            v-for="atalho in atalhosData"
            :key="atalho.valor"
            type="button"
            class="quick-date"
            :class="{ active: selectedDate === atalho.valor }"
            :disabled="!eadminsera && atalho.valor < minDate"
            @click="selecionarData(atalho.valor)"
          >
            <span>{{ atalho.rotulo }}</span>
            <strong>{{ atalho.dia }}</strong>
          </button>
        </div>
      </section>

      <section class="summary-grid" aria-label="Resumo da agenda">
        <button type="button" class="summary-card" :class="{ active: filtroVisual === 'todos' }" @click="filtroVisual = 'todos'">
          <span>Horários</span>
          <strong>{{ groupedItems.length }}</strong>
        </button>
        <button type="button" class="summary-card" :class="{ active: filtroVisual === 'todos' }" @click="filtroVisual = 'todos'">
          <span>Total de Roteiros</span>
          <strong>{{ visibleItems.length }}</strong>
        </button>
        <button
          type="button"
          class="summary-card summary-available"
          :class="{ active: filtroVisual === 'disponiveis' }"
          @click="alternarFiltroVisual('disponiveis')"
        >
          <span>Disponíveis</span>
          <span v-if="filtroVisual === 'disponiveis'" class="filter-check" aria-hidden="true">✓</span>
          <strong>{{ totalDisponiveis }}</strong>
        </button>
        <button
          type="button"
          class="summary-card summary-full"
          :class="{ active: filtroVisual === 'esgotados' }"
          @click="alternarFiltroVisual('esgotados')"
        >
          <span>Esgotados</span>
          <span v-if="filtroVisual === 'esgotados'" class="filter-check" aria-hidden="true">✓</span>
          <strong>{{ totalEsgotados }}</strong>
        </button>
      </section>

      <section v-if="errorMessage" class="feedback-state error-state">
        <h2>Não foi possível carregar a agenda</h2>
        <p>{{ errorMessage }}</p>
      </section>

      <section v-else-if="!groupedItems.length" class="feedback-state empty-state">
        <h2>Agenda indisponível</h2>
        <p>Não encontramos roteiros ativos para a data selecionada.</p>
      </section>

      <template v-else>
        <nav class="time-tabs" aria-label="Horários disponíveis">
          <p>Navegue pelos horários disponíveis</p>
          <button
            v-for="group in groupedItems"
            :key="group.horario"
            type="button"
            class="time-tab"
            :class="{ active: selectedHorario === group.horario }"
            @click="selectedHorario = group.horario"
          >
            <span>{{ group.horario }}</span>
          </button>
        </nav>

        <section class="slot-panel" aria-label="Roteiros do horário selecionado">
          <div class="slot-header">
            <div>
              <p class="eyebrow">Horário</p>
              <h2>{{ selectedHorario }}</h2>
            </div>
            <p>{{ selectedGroupItemsFiltrados.length }} {{ selectedGroupItemsFiltrados.length === 1 ? 'roteiro' : 'roteiros' }}</p>
          </div>

          <div v-if="selectedGroupItemsFiltrados.length" class="routes-grid">
            <article
              v-for="item in selectedGroupItemsFiltrados"
              :key="item.codigo"
              class="route-card"
              :class="{
                unavailable: !roteiroDisponivel(item),
                inactive: roteiroFechado(item),
              }"
            >
              <div class="route-card-header">
                <div>
                  <span class="route-position">Posição {{ getLastTwoDigits(item.codigo) }}</span>
                  <h3>{{ valorCampo(item.titulo) }}</h3>
                </div>
                <span class="status-badge" :class="statusClass(item)">
                  {{ statusLabel(item) }}
                </span>
              </div>

              <div class="route-time">
                <span>Horário</span>
                <strong>{{ formatHorario(item.codigo) }} · {{ formattedSelectedDate }}</strong>
              </div>

              <dl class="route-details">
                <div>
                  <dt>Vagas</dt>
                  <dd>{{ valorCampo(item.vagas) }}</dd>
                </div>
                <div>
                  <dt>Duração</dt>
                  <dd>{{ valorCampo(item.duracao) }}</dd>
                </div>
                <div class="details-wide">
                  <dt>Faixa etária/Restrições</dt>
                  <dd>{{ valorCampo(item.restricoes) }}</dd>
                </div>
                <div class="details-wide">
                  <dt>Descrição</dt>
                  <dd>{{ valorCampo(item.descricao) }}</dd>
                </div>
              </dl>

              <div class="route-actions">
                <button
                  type="button"
                  class="btn btn-success"
                  :disabled="!roteiroDisponivel(item)"
                  @click="reservar(item.codigo, email)"
                >
                  Reservar
                </button>
                <button
                  v-if="eadminsera"
                  type="button"
                  class="btn btn-dark"
                  :disabled="!roteiroDisponivelParaAdmin(item)"
                  @click="reservar2(item.codigo, email)"
                >
                  Reserva ADM
                </button>
              </div>

              <p v-if="roteiroFechado(item) && eadminsera" class="admin-note">
                Este roteiro está fechado na agenda pública.
              </p>
            </article>
          </div>
          <div v-else class="slot-empty">
            <h3>Nenhum roteiro neste filtro</h3>
            <p>Troque o filtro de disponibilidade ou selecione outro horário.</p>
          </div>
        </section>
      </template>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, computed, watch } from "vue";
import router from "@/router";
import axios from "axios";
import { getCurrentUser } from "@/services/authService";
import { isMasterFull } from "@/utils/auth";

export default {
  setup() {
    const minDate = ref("");
    const selectedDate = ref("");
    const tempDate = ref("");
    const selectedHorario = ref("");
    const items = ref([]);
    const email = ref("");
    const isLoading = ref(false);
    const errorMessage = ref("");
    const filtroVisual = ref("todos");
    const eadminsera = isMasterFull();

    const valorCampo = (valor) => {
      if (Array.isArray(valor)) {
        return valor.length ? valor.join(", ") : "Informações não preenchidas";
      }

      return valor || valor === 0 ? valor : "Informações não preenchidas";
    };

    const calculateMinDate = () => {
      const today = new Date();
      const minDateValue = new Date(today.setDate(today.getDate() + 2));
      minDate.value = minDateValue.toISOString().split("T")[0];
      tempDate.value = minDate.value;
      selectedDate.value = minDate.value;
    };

    const formatDateInput = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const criarDataLocal = (date) => {
      if (!date) return null;

      const [year, month, day] = date.split("-").map(Number);
      if (!year || !month || !day) return null;

      return new Date(year, month - 1, day);
    };

    const adicionarDias = (date, quantidade) => {
      const data = criarDataLocal(date);
      if (!data) return "";

      data.setDate(data.getDate() + quantidade);
      return formatDateInput(data);
    };

    const formatDateToDDMMYYYY = (date) => {
      if (!date) return "";

      const [year, month, day] = date.split("-");
      if (!year || !month || !day) return date;

      return `${day}/${month}/${year}`;
    };

    const getDayOfWeek = (date) => {
      if (!date) return "";

      const days = [
        "domingo",
        "segunda-feira",
        "terça-feira",
        "quarta-feira",
        "quinta-feira",
        "sexta-feira",
        "sábado",
      ];
      const dayIndex = new Date(`${date}T00:00:00-03:00`).getDay();
      return days[dayIndex] || "";
    };

    const formatHorario = (codigo) => {
      const texto = String(codigo ?? "");
      const hora = texto.substring(8, 10);
      const minutos = texto.substring(10, 12);

      if (!hora || !minutos) {
        return "Horário não informado";
      }

      return `${hora}:${minutos}`;
    };

    const getLastTwoDigits = (codigo) => String(codigo ?? "").slice(-2);

    const normalizarStatus = (item) => item?.aprovacao ?? item?.estadoaprovacao ?? null;

    const roteiroFechado = (item) => String(item?.ativo) === "0";

    const roteiroDisponivel = (item) => {
      const status = normalizarStatus(item);
      return !roteiroFechado(item) && (status === "0" || status === "4" || status === null || status === undefined || status === "");
    };

    const roteiroDisponivelParaAdmin = (item) => {
      const status = normalizarStatus(item);
      return status === "0" || status === "4" || status === null || status === undefined || status === "";
    };

    const statusLabel = (item) => {
      if (roteiroFechado(item)) return "Fechado";
      if (roteiroDisponivel(item)) return "Disponível";

      const status = normalizarStatus(item);
      if (status === "2") return "Reservado";
      if (status === "4") return "Cancelamento solicitado";

      return "Esgotado";
    };

    const statusClass = (item) => {
      if (roteiroFechado(item)) return "status-fechado";
      if (roteiroDisponivel(item)) return "status-disponivel";

      return "status-esgotado";
    };

    const fetchData = async () => {
      if (!selectedDate.value) return;

      const formattedDate = formatDateToDDMMYYYY(selectedDate.value);
      isLoading.value = true;
      errorMessage.value = "";

      try {
        const response = await axios.get("https://museucatavento.org.br/sistema-agendamento/apiroteiros2.php", {
          params: { datedisplay: formattedDate },
        });

        const data = Array.isArray(response.data) ? response.data : [];
        items.value = data.map((item) => ({
          ...item,
          ativo: String(item.ativo ?? "1"),
        }));
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        items.value = [];
        errorMessage.value = "Tente novamente em alguns instantes.";
      } finally {
        isLoading.value = false;
      }
    };

    const respostaReservaValida = (valor) => {
      const numero = Number(valor);
      return Number.isFinite(numero) && String(valor).trim() !== "";
    };

    const reservar = async (codigo, userEmail) => {
      try {
        const response = await axios.post(
          "https://museucatavento.org.br/sistema-agendamento/apiagendar.php",
          { codigo, email: userEmail },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (respostaReservaValida(response.data)) {
          localStorage.setItem("codigoemreserva", codigo);
          localStorage.setItem("ticket", response.data);
          alert("Vamos para o preenchimento das demais informações. Clique em OK.");
          router.push("/formulario");
        } else {
          alert(response.data);
        }

        await fetchData();
      } catch (error) {
        console.error("Erro ao reservar:", error);
        alert("Não foi possível reservar este roteiro. Tente novamente em instantes.");
      }
    };

    const reservar2 = async (codigo, userEmail) => {
      try {
        const response = await axios.post(
          "https://museucatavento.org.br/sistema-agendamento/apiagendar2.php",
          { codigo, email: userEmail },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (respostaReservaValida(response.data)) {
          localStorage.setItem("codigoemreserva", codigo);
          localStorage.setItem("ticket", response.data);
          alert("Reserva administrativa realizada com sucesso.");
          router.push("/registros");
        } else {
          alert(response.data);
        }

        await fetchData();
      } catch (error) {
        console.error("Erro ao reservar:", error);
        alert("Não foi possível realizar a reserva administrativa.");
      }
    };

    const visibleItems = computed(() => {
      return items.value.filter((item) => !roteiroFechado(item) || eadminsera);
    });

    const groupedItems = computed(() => {
      const groups = {};

      visibleItems.value.forEach((item) => {
        const horario = formatHorario(item.codigo);
        if (!groups[horario]) {
          groups[horario] = [];
        }
        groups[horario].push(item);
      });

      return Object.keys(groups)
        .sort((a, b) => a.localeCompare(b))
        .map((horario) => ({
          horario,
          items: groups[horario].sort((a, b) => String(a.codigo).localeCompare(String(b.codigo))),
        }));
    });

    const selectedGroupItems = computed(() => {
      return groupedItems.value.find((group) => group.horario === selectedHorario.value)?.items || [];
    });

    const selectedGroupItemsFiltrados = computed(() => {
      if (filtroVisual.value === "disponiveis") {
        return selectedGroupItems.value.filter((item) => roteiroDisponivel(item));
      }

      if (filtroVisual.value === "esgotados") {
        return selectedGroupItems.value.filter((item) => !roteiroDisponivel(item) && !roteiroFechado(item));
      }

      return selectedGroupItems.value;
    });

    const totalDisponiveis = computed(() => visibleItems.value.filter((item) => roteiroDisponivel(item)).length);

    const totalEsgotados = computed(
      () => visibleItems.value.filter((item) => !roteiroDisponivel(item) && !roteiroFechado(item)).length
    );

    const formattedSelectedDate = computed(() => formatDateToDDMMYYYY(selectedDate.value));

    const dayOfWeek = computed(() => getDayOfWeek(selectedDate.value));

    const podeVoltarData = computed(() => {
      if (eadminsera) return Boolean(selectedDate.value);
      return Boolean(selectedDate.value) && selectedDate.value > minDate.value;
    });

    const atalhosData = computed(() => {
      const base = selectedDate.value || minDate.value;
      const offsets = [-2, -1, 0, 1, 2];

      return offsets
        .map((offset) => {
          const valor = adicionarDias(base, offset);
          return {
            valor,
            rotulo: offset === 0 ? "Selecionada" : getDayOfWeek(valor),
            dia: formatDateToDDMMYYYY(valor).slice(0, 5),
          };
        })
        .filter((atalho) => eadminsera || atalho.valor >= minDate.value);
    });

    const atualizarData = () => {
      const hoje = new Date();
      const dataMinima = new Date(hoje);
      dataMinima.setDate(hoje.getDate() + 2);
      dataMinima.setHours(0, 0, 0, 0);

      const tempDateObject = new Date(`${tempDate.value}T00:00:00`);
      if (eadminsera || tempDateObject >= dataMinima) {
        selectedDate.value = tempDate.value;
        fetchData();
      } else {
        alert("A data selecionada deve ser, no mínimo, 2 dias a partir de hoje.");
      }
    };

    const selecionarData = (date) => {
      tempDate.value = date;
      atualizarData();
    };

    const mudarData = (direcao) => {
      const proximaData = adicionarDias(selectedDate.value || tempDate.value || minDate.value, direcao);
      if (!eadminsera && proximaData < minDate.value) return;

      selecionarData(proximaData);
    };

    const alternarFiltroVisual = (filtro) => {
      filtroVisual.value = filtroVisual.value === filtro ? "todos" : filtro;
    };

    watch(groupedItems, (groups) => {
      if (!groups.length) {
        selectedHorario.value = "";
        return;
      }

      const horarioExiste = groups.some((group) => group.horario === selectedHorario.value);
      if (!horarioExiste) {
        selectedHorario.value = groups[0].horario;
      }
    });

    onMounted(() => {
      calculateMinDate();
      email.value = getCurrentUser();
      fetchData();
    });

    return {
      dayOfWeek,
      email,
      eadminsera,
      errorMessage,
      alternarFiltroVisual,
      atalhosData,
      fetchData,
      filtroVisual,
      formatHorario,
      formattedSelectedDate,
      getLastTwoDigits,
      groupedItems,
      isLoading,
      items,
      minDate,
      reservar,
      reservar2,
      roteiroDisponivel,
      roteiroDisponivelParaAdmin,
      roteiroFechado,
      selectedDate,
      selectedGroupItems,
      selectedGroupItemsFiltrados,
      selectedHorario,
      selecionarData,
      statusClass,
      statusLabel,
      tempDate,
      totalDisponiveis,
      totalEsgotados,
      atualizarData,
      valorCampo,
      visibleItems,
      mudarData,
      podeVoltarData,
    };
  },
};
</script>

<style scoped>
.schedule-page {
  background: #f5f7fa;
  min-height: 100vh;
  padding: clamp(1.25rem, 3vw, 2.5rem) 0 3rem;
  text-align: left;
}

.schedule-shell {
  width: min(1320px, calc(100% - 2rem));
  margin: 0 auto;
}

.schedule-header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.eyebrow {
  color: var(--catavento-orange, #cd5f3f);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.schedule-header h1 {
  color: var(--catavento-blue, #165a96);
  margin: 0;
}

.header-copy {
  color: var(--catavento-muted, #667085);
  max-width: 720px;
  margin: 0.6rem 0 0;
}

.service-notice {
  background: #eff6ff;
  border: 1px solid #c9ddf3;
  border-radius: 8px;
  color: var(--catavento-blue, #165a96);
  font-weight: 800;
  margin: 0.85rem 0 0;
  max-width: 760px;
  padding: 0.85rem 1rem;
}

.user-panel,
.date-panel,
.summary-card,
.slot-panel,
.feedback-state {
  background: var(--catavento-surface, #fff);
  border: 1px solid var(--catavento-border, #d9e1ea);
  border-radius: var(--catavento-radius, 8px);
  box-shadow: 0 8px 22px rgba(20, 30, 45, 0.08);
}

.user-panel {
  min-width: min(360px, 100%);
  padding: 1rem;
}

.user-panel span,
.selected-date span,
.summary-card span {
  color: var(--catavento-muted, #667085);
  display: block;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.user-panel strong,
.selected-date strong {
  color: var(--catavento-text, #243447);
  display: block;
  margin-top: 0.25rem;
  overflow-wrap: anywhere;
}

.date-panel {
  align-items: stretch;
  display: grid;
  grid-template-columns: 48px minmax(220px, 1fr) minmax(190px, auto) 48px;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
}

.date-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 800;
  justify-content: center;
}

.date-nav-button {
  align-items: center;
  align-self: stretch;
  background: #f8fafc;
  border: 1px solid var(--catavento-border, #d9e1ea);
  border-radius: 8px;
  color: var(--catavento-blue, #165a96);
  cursor: pointer;
  display: inline-flex;
  font-size: 2rem;
  font-weight: 800;
  justify-content: center;
  min-height: 48px;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.date-nav-button:hover:not(:disabled) {
  background: #eff6ff;
  border-color: var(--catavento-blue, #165a96);
  transform: translateY(-1px);
}

.date-nav-button:disabled {
  color: #98a2b3;
  cursor: not-allowed;
  opacity: 0.65;
}

.selected-date small {
  color: var(--catavento-muted, #667085);
  display: block;
  margin-top: 0.1rem;
  text-transform: capitalize;
}

.quick-dates {
  display: grid;
  gap: 0.5rem;
  grid-column: 1 / -1;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.quick-date {
  background: #fff;
  border: 1px solid var(--catavento-border, #d9e1ea);
  border-radius: 8px;
  color: var(--catavento-text, #243447);
  cursor: pointer;
  min-height: 58px;
  padding: 0.55rem 0.7rem;
  text-align: left;
}

.quick-date:hover:not(:disabled),
.quick-date.active {
  border-color: var(--catavento-blue, #165a96);
  box-shadow: 0 8px 18px rgba(20, 30, 45, 0.1);
}

.quick-date.active {
  background: #eff6ff;
}

.quick-date span {
  color: var(--catavento-muted, #667085);
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: capitalize;
}

.quick-date strong {
  color: var(--catavento-text, #243447);
  display: block;
  margin-top: 0.15rem;
}

.quick-date:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-card {
  border-left: 5px solid var(--catavento-blue, #165a96);
  cursor: pointer;
  position: relative;
  min-height: 82px;
  padding: 0.85rem;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  width: 100%;
}

.summary-card:hover,
.summary-card.active {
  border-color: var(--catavento-blue, #165a96);
  box-shadow: 0 10px 24px rgba(20, 30, 45, 0.12);
  transform: translateY(-1px);
}

.summary-card.active {
  outline: 3px solid rgba(22, 90, 150, 0.18);
}

.summary-available.active {
  background: #e6f4ed;
  border-color: #1f8a5b;
}

.summary-full.active {
  background: #fde8e8;
  border-color: #c93434;
}

.filter-check {
  align-items: center;
  border-radius: 999px;
  color: #fff !important;
  display: inline-flex !important;
  font-size: 0.85rem !important;
  font-weight: 900 !important;
  height: 24px;
  justify-content: center;
  position: absolute;
  right: 0.75rem;
  text-transform: none !important;
  top: 0.75rem;
  width: 24px;
}

.summary-available .filter-check {
  background: #1f8a5b;
}

.summary-full .filter-check {
  background: #c93434;
}

.summary-card strong {
  color: var(--catavento-text, #243447);
  display: block;
  font-size: 1.7rem;
  line-height: 1;
  margin-top: 0.35rem;
}

.summary-available {
  border-left-color: #1f8a5b;
}

.summary-full {
  border-left-color: #c93434;
}

.time-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin: 1.25rem 0;
}

.time-tab {
  align-items: center;
  background: #fff;
  border: 1px solid var(--catavento-border, #d9e1ea);
  border-radius: 8px;
  color: var(--catavento-text, #243447);
  cursor: pointer;
  display: inline-flex;
  gap: 0.65rem;
  min-height: 46px;
  padding: 0.65rem 0.85rem;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.time-tab:hover,
.time-tab.active {
  border-color: var(--catavento-blue, #165a96);
  box-shadow: 0 8px 18px rgba(20, 30, 45, 0.12);
  transform: translateY(-1px);
}

.time-tab.active {
  background: var(--catavento-blue, #165a96);
  color: #fff;
}

.time-tab span,
.time-tab strong {
  font-weight: 800;
}

.time-tab strong {
  align-items: center;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  display: inline-flex;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;
  padding: 0 0.4rem;
}

.slot-panel {
  padding: 1rem;
}

.slot-header {
  align-items: center;
  border-bottom: 1px solid var(--catavento-border, #d9e1ea);
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
}

.slot-header h2,
.slot-header p {
  margin: 0;
}

.slot-header h2 {
  color: var(--catavento-blue, #165a96);
}

.slot-header > p {
  color: var(--catavento-muted, #667085);
  font-weight: 800;
}

.routes-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.route-card {
  border: 1px solid var(--catavento-border, #d9e1ea);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-height: 100%;
  padding: 1rem;
}

.route-card.unavailable {
  background: #fbfcfe;
}

.route-card.inactive {
  border-style: dashed;
}

.route-card-header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.route-card h3 {
  color: var(--catavento-text, #243447);
  font-size: 1.05rem;
  font-weight: 800;
  margin: 0.2rem 0 0;
}

.route-position {
  color: var(--catavento-muted, #667085);
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.status-badge {
  border-radius: 999px;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.76rem;
  font-weight: 800;
  justify-content: center;
  min-height: 28px;
  padding: 0.25rem 0.65rem;
}

.status-disponivel {
  background: #e6f4ed;
  color: #176847;
}

.status-esgotado {
  background: #fde8e8;
  color: #9b1c1c;
}

.status-fechado {
  background: #eef1f5;
  color: #475467;
}

.route-time {
  background: #eff6ff;
  border: 1px solid #c9ddf3;
  border-radius: 6px;
  padding: 0.75rem;
}

.route-time span,
dt {
  color: var(--catavento-muted, #667085);
  display: block;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.route-time strong {
  color: var(--catavento-blue, #165a96);
  display: block;
  margin-top: 0.2rem;
}

.route-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 0;
}

.details-wide {
  grid-column: 1 / -1;
}

dt,
dd {
  margin: 0;
}

dd {
  color: var(--catavento-text, #243447);
  overflow-wrap: anywhere;
}

.route-actions {
  display: grid;
  gap: 0.6rem;
  margin-top: auto;
}

.route-actions .btn:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.admin-note {
  color: var(--catavento-muted, #667085);
  font-size: 0.85rem;
  margin: 0;
}

.feedback-state {
  padding: 2rem;
  text-align: center;
}

.slot-empty {
  background: #f8fafc;
  border: 1px dashed var(--catavento-border, #d9e1ea);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
}

.slot-empty h3 {
  color: var(--catavento-text, #243447);
  font-size: 1.1rem;
  margin-bottom: 0.35rem;
}

.slot-empty p {
  color: var(--catavento-muted, #667085);
  margin: 0;
}

.feedback-state h2 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
}

.feedback-state p {
  color: var(--catavento-muted, #667085);
  margin: 0;
}

.error-state {
  border-color: #f0b4b4;
}

.loading-overlay {
  align-items: center;
  background: rgba(17, 24, 39, 0.55);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1200;
}

.loading-box {
  align-items: center;
  background: #fff;
  border-radius: var(--catavento-radius, 8px);
  box-shadow: var(--catavento-shadow, 0 10px 28px rgba(20, 30, 45, 0.12));
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  text-align: center;
  width: min(360px, 100%);
}

.loading-box p {
  margin: 0;
}

@media (max-width: 1100px) {
  .routes-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .schedule-header,
  .slot-header {
    align-items: stretch;
    flex-direction: column;
  }

  .date-panel,
  .summary-grid {
    grid-template-columns: 48px 1fr 48px;
  }

  .selected-date {
    grid-column: 2;
    grid-row: 2;
  }

  .date-field {
    grid-column: 2;
  }

  .quick-dates,
  .summary-grid {
    grid-column: 1 / -1;
  }

  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 767px) {
  .schedule-shell {
    width: min(100% - 1rem, 1320px);
  }

  .date-panel,
  .summary-grid,
  .routes-grid,
  .route-details {
    grid-template-columns: 1fr;
  }

  .date-nav-button,
  .date-field,
  .selected-date,
  .quick-dates,
  .summary-grid {
    grid-column: auto;
    grid-row: auto;
  }

  .quick-dates {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .time-tab {
    flex: 1 1 44%;
    justify-content: space-between;
  }

  .route-card-header {
    flex-direction: column;
  }
}
</style>
